import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import CommonPageLayout from "../../components/commonPageLayout/commonPageLayout";
import {
  Typography as MuiTypography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box as MuiBox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert,
  CircularProgress,
  Button as MuiButton,
  Link as MuiLink
} from "@mui/material";
import CustomButton from "../../components/CommonButton/CommonButton";
import CloseIcon from "@mui/icons-material/Close";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
// removed unused SearchIcon

import {
  GET_MEMBERS_PATH,
  SEARCH_MEMBERS_PATH,
  ADD_CREDIT_PATH,
  ADD_POINTS_PATH
} from "../../constants/apipaths";
import CustomTableWithPagination from "../../components/customTable/CustomTable";
import { styled } from "@mui/system";
import CopyToClipboardWrapper from "../../components/copyToClipboardWrapper/CopyToClipboardWrapper";
import {
  getFormattedDate,
  getFormattedDateWithoutTime
} from "../../Utils/commonFunction";
import RefreshButton from "../../components/RefreshButton/RefreshButton";
import SearchBox from "../../components/searchBox/SearchBox";
import { useAppSelector } from "../../redux/hooks";

const Typography = styled(MuiTypography)``;

// Styled components for search
const BoxTitle = styled(MuiBox)`
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
  align-items: center;
`;

const Button = styled(MuiButton)`
  &.custom_disabled {
    background-color: grey !important;
  }

  &.blue_button {
    background-color: #192f67 !important;
    color: white !important;

    &:hover {
      background-color: #2a3f7a !important;
    }
  }
`;

const SearchBoxContainer = styled(MuiBox)`
  display: flex;
  gap: 10px;
  align-items: baseline;
`;

const TypographyTitle = styled(MuiTypography)`
  color: var(--light-text-dark, rgba(0, 0, 0, 0.88));
  font-family: "Montserrat", sans-serif;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  margin-bottom: 16px;
`;

interface Member {
  UserId: string;
  NAME?: string;
  Email?: string;
  Mobile?: string;
  DateOfBirth?: string;
  PostCode?: string | null;
  DateSignUp?: string;
  DateLastActivity?: string;
  Credits?: string;
  Points?: string;
  HasEmail?: number;
}

interface AddCreditRequest {
  UserId: string;
  Credit: number;
}

interface AddPointsRequest {
  UserId: string;
  Points: number;
}

const MembersPage = () => {
  const navigate = useNavigate();
  let currentBusiness: any = localStorage?.getItem("currentBusiness");
  const state: any = {
    token: localStorage?.getItem("businessAccessToken"),
    currentBusiness: currentBusiness ? JSON.parse(currentBusiness) : null
  };

  const [members, setMembers] = useState<Member[]>([]);
  const [filteredMembers, setFilteredMembers] = useState<Member[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [isSearchMode, setIsSearchMode] = useState(false);
  console.log("🚀 ~ MembersPage ~ members:", members);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [payloadSize, setPayloadSize] = useState(10);

  const [openModal, setOpenModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [actionType, setActionType] = useState<"credit" | "points">("credit");
  const [amount, setAmount] = useState("");
  // removed unused apiStatus state
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState<{
    message: string;
    type: "success" | "error" | "info" | "warning";
    supportLink?: boolean;
  }>({
    message: "",
    type: "success"
  });
  const [serverTotalCount, setServerTotalCount] = useState<number>(0);
  const [selectedChildBusinessId, setSelectedChildBusinessId] = useState<string>("");
  const [searchError, setSearchError] = useState<string>("");
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const previousBusinessIdRef = useRef<string | undefined>(state?.currentBusiness?.BusinessId);

  // Global billing / restriction state from Redux
  const { isNonPaying, isBillingLoading } = useAppSelector(
    (s) => s.businessAccess
  );

  // Get userDetails from localStorage
  const getUserDetails = () => {
    try {
      const userDetailsStr = localStorage.getItem("userDetails");
      return userDetailsStr ? JSON.parse(userDetailsStr) : null;
    } catch (error) {
      console.error("Error parsing userDetails from localStorage:", error);
      return null;
    }
  };

  // Check if current business is a parent business
  const isParentBusiness = () => {
    const userDetails = getUserDetails();
    return userDetails?.currentBusiness?.IsParent === 1;
  };

  // Get child businesses (where IsParent === 0)
  const getChildBusinesses = () => {
    const userDetails = getUserDetails();
    if (!userDetails?.allBusiness) return [];
    return userDetails.allBusiness.filter((business: any) => business.IsParent === 0);
  };

  // Validate email using regex
  const validateEmail = (email: string): boolean => {
    const regex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;
    const isValid = regex.test(email);
    console.log('ValidEmailAddress: ' + isValid);
    return isValid;
  };

  // Check if input looks like an email (contains @)
  const looksLikeEmail = (input: string): boolean => {
    return input.includes("@");
  };

  // define refreshMembers and then call it in effects and actions
  const refreshMembers = useCallback(async (pageNumber?: number, pageSize?: number) => {
    try {
      setIsLoading(true);
      setIsSearchMode(false);

      console.log(
        "🔍 Fetching members for business:",
        state?.currentBusiness?.BusinessId
      );
      console.log("🔍 Using token:", state.token ? "Token exists" : "No token");

      const qs = (pageNumber && pageSize)
        ? `?PageSize=${pageSize}&PageNumber=${pageNumber}`
        : "";
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}${GET_MEMBERS_PATH}/${state?.currentBusiness?.BusinessId}${qs}`,
        {
          headers: {
            Authorization: `Bearer ${state.token}`,
            "Content-Type": "application/json"
          }
        }
      );

      console.log("🚀 ~ refreshMembers ~ response:", response);
      console.log("🚀 ~ response.data:", response.data);

      if (response?.data) {
        const payload = response.data;
        
        // Handle wrapped shape: { success: true, data: [...], totalCount: number, ... }
        if (payload?.success && Array.isArray(payload?.data)) {
          setMembers(payload.data);
          setServerTotalCount(typeof payload.totalCount === "number" ? payload.totalCount : payload.data.length);
          console.log("✅ Members set successfully. Total count:", payload.totalCount);
        }
        // Handle direct array response (backward compatibility)
        else if (Array.isArray(payload)) {
          setMembers(payload);
          // Try to infer total count from first row if present
          const inferred = payload?.[0]?.TotalCount;
          setServerTotalCount(typeof inferred === "number" ? inferred : payload.length);
          console.log("✅ Members set successfully (array format). Total count:", typeof inferred === "number" ? inferred : payload.length);
        }
        // Handle wrapped shape without success field: { data: [...], totalCount: number }
        else if (Array.isArray(payload?.data)) {
          setMembers(payload.data);
          const total = payload?.totalCount || payload?.TotalCount;
          setServerTotalCount(typeof total === "number" ? total : payload.data.length);
          console.log("✅ Members set successfully (wrapped format). Total count:", total);
        } else {
          setMembers([]);
          setServerTotalCount(0);
          console.log("⚠️ Unexpected response format, setting empty array");
        }
      } else {
        setMembers([]);
        setServerTotalCount(0);
        console.log("⚠️ No data in response, setting empty array");
      }
    } catch (error) {
      console.error("❌ Error fetching members:", error);
    } finally {
      setIsLoading(false);
    }
  }, [state?.currentBusiness?.BusinessId, state.token]);

  useEffect(() => {
    // Clear search and sort when business changes
    const currentBusinessId = state?.currentBusiness?.BusinessId;
    if (previousBusinessIdRef.current !== currentBusinessId && (isSearchMode || searchQuery)) {
      setSearchQuery("");
      setIsSearchMode(false);
      setSortField(null);
      setSortDirection("asc");
    }
    previousBusinessIdRef.current = currentBusinessId;
  }, [state?.currentBusiness?.BusinessId, isSearchMode, searchQuery]);

  useEffect(() => {
    // Refresh members when business changes (always) or when pagination changes (only if not searching)
    // Note: We don't include searchQuery in dependencies to prevent API calls while typing
    if (!isSearchMode) {
      refreshMembers(currentPage + 1, payloadSize);
    }
  }, [state?.currentBusiness?.BusinessId, currentPage, payloadSize, refreshMembers, isSearchMode]);

  // Search members using API (no pagination support)
  const searchMembers = useCallback(
    async (keyword: string) => {
      if (!keyword.trim()) {
        setIsSearchMode(false);
        refreshMembers(currentPage + 1, payloadSize);
        return;
      }

      try {
        setIsSearching(true);
        setIsSearchMode(true);

        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}${SEARCH_MEMBERS_PATH}/${state?.currentBusiness?.BusinessId}/search/user/${encodeURIComponent(keyword)}`,
          {
            headers: {
              Authorization: `Bearer ${state.token}`,
              "Content-Type": "application/json"
            }
          }
        );

        if (response?.data) {
          const payload = response.data;
          
          // Handle wrapped shape: { success: true, data: [...], totalCount: number, ... }
          if (payload?.success && Array.isArray(payload?.data)) {
            setFilteredMembers(payload.data);
            setServerTotalCount(typeof payload.totalCount === "number" ? payload.totalCount : payload.data.length);
          }
          // Handle direct array response (backward compatibility)
          else if (Array.isArray(payload)) {
            setFilteredMembers(payload);
            const inferred = payload?.[0]?.TotalCount;
            setServerTotalCount(typeof inferred === "number" ? inferred : payload.length);
          }
          // Handle wrapped shape without success field: { data: [...], totalCount: number }
          else if (Array.isArray(payload?.data)) {
            setFilteredMembers(payload.data);
            const total = payload?.totalCount || payload?.TotalCount;
            setServerTotalCount(typeof total === "number" ? total : payload.data.length);
          } else {
            setFilteredMembers([]);
            setServerTotalCount(0);
          }
        } else {
          setFilteredMembers([]);
          setServerTotalCount(0);
        }
      } catch (error) {
        console.error("❌ Error searching members:", error);
        setFilteredMembers([]);
        setServerTotalCount(0);
      } finally {
        setIsSearching(false);
      }
    },
    [state?.currentBusiness?.BusinessId, state.token, currentPage, payloadSize, refreshMembers]
  );

  // Handle search button click
  const handleSearch = (query: string) => {
    // Clear previous error
    setSearchError("");

    // Check if input looks like an email
    if (looksLikeEmail(query)) {
      // Validate email format
      if (!validateEmail(query)) {
        setSearchError("Please enter a valid email address");
        return;
      }
    }

    setCurrentPage(0);
    searchMembers(query);
  };

  // Handle Enter key press
  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      handleSearch(searchQuery);
    }
  };

  // Clear search and show all members
  const clearSearch = () => {
    setSearchQuery("");
    setIsSearchMode(false);
    setSearchError("");
    setCurrentPage(0);
    setSortField(null);
    setSortDirection("asc");
    refreshMembers(1, payloadSize);
  };

  // Sort function to handle different data types
  const sortMembers = useCallback((membersToSort: Member[], field: string | null, direction: "asc" | "desc"): Member[] => {
    if (!field) return membersToSort;

    return [...membersToSort].sort((a, b) => {
      let aValue: any = a[field as keyof Member];
      let bValue: any = b[field as keyof Member];

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Handle date fields
      if (field === "DateSignUp" || field === "DateLastActivity" || field === "DateOfBirth") {
        const aDate = new Date(aValue).getTime();
        const bDate = new Date(bValue).getTime();
        return direction === "asc" ? aDate - bDate : bDate - aDate;
      }

      // Handle numeric fields (Credits, Points)
      if (field === "Credits" || field === "Points") {
        const aNum = parseFloat(aValue) || 0;
        const bNum = parseFloat(bValue) || 0;
        return direction === "asc" ? aNum - bNum : bNum - aNum;
      }

      // Handle boolean/number fields (HasEmail)
      if (field === "HasEmail") {
        const aNum = Number(aValue) || 0;
        const bNum = Number(bValue) || 0;
        return direction === "asc" ? aNum - bNum : bNum - aNum;
      }

      // Handle string fields (NAME, Email, Mobile, PostCode)
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      if (aStr < bStr) return direction === "asc" ? -1 : 1;
      if (aStr > bStr) return direction === "asc" ? 1 : -1;
      return 0;
    });
  }, []);

  // Handle column sort
  const handleSort = (field: string) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection("asc");
    }
    // Reset to first page when sorting changes
    setCurrentPage(0);
  };

  // Update filtered members when members data changes (only if not in search mode)
  useEffect(() => {
    if (!isSearchMode) {
      setFilteredMembers(members);
    }
  }, [members, isSearchMode]);

  // Reset to first page on search query change
  useEffect(() => {
    setCurrentPage(0);
  }, [searchQuery]);

  // Clear search error when user starts typing
  useEffect(() => {
    setSearchError("");
  }, [searchQuery]);

  const handleAddCredit = async () => {
    if (!selectedMember || !amount) return;

    // Validate amount
    const creditAmount = parseFloat(amount);
    if (creditAmount <= 0) {
      setNotificationMessage({
        message: "Credit amount must be greater than 0",
        type: "warning"
      });
      return;
    }

    // Validate child business selection if parent business
    if (isParentBusiness() && !selectedChildBusinessId) {
      setNotificationMessage({
        message: "Please select a child business",
        type: "warning"
      });
      return;
    }

    setIsModalLoading(true);
    try {
      const request: AddCreditRequest = {
        UserId: selectedMember.UserId,
        Credit: creditAmount
      };

      // Use selected child business ID if parent, otherwise use current business ID
      const businessId = isParentBusiness() && selectedChildBusinessId
        ? selectedChildBusinessId
        : state?.currentBusiness?.BusinessId;

      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}${ADD_CREDIT_PATH}/${businessId}`,
        request,
        {
          headers: {
            Authorization: `Bearer ${state.token}`,
            "Content-Type": "application/json"
          }
        }
      );

      setNotificationMessage({
        message: `Successfully added $${creditAmount} credits to ${
          selectedMember.NAME || selectedMember.Email || "member"
        }`,
        type: "success"
      });

      // Refresh members data
      refreshMembers();
      handleCloseModal();
    } catch (error: any) {
      console.error("Error adding credit:", error);

      // Handle different types of errors
      let errorMessage = "Failed to add credit. Please try again.";

      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const data = error.response.data;

        if (status === 400) {
          errorMessage =
            data?.message || "Invalid request. Please check the amount.";
        } else if (status === 401) {
          errorMessage = "Unauthorized. Please check your authentication.";
        } else if (status === 403) {
          errorMessage =
            "Access denied. You don't have permission to perform this action.";
        } else if (status === 404) {
          errorMessage = "Member not found or business not found.";
        } else if (status === 500) {
          errorMessage = "Server error. Please try again later.";
        } else if (data?.message) {
          errorMessage = data.message;
        }
      } else if (error.request) {
        // Network error
        errorMessage =
          "Network error. Please check your connection and try again.";
      }

      setNotificationMessage({
        message: errorMessage,
        type: "error"
      });
    } finally {
      setIsModalLoading(false);
    }
  };

  const handleAddPoints = async () => {
    if (!selectedMember || !amount) return;

    // Validate amount
    const pointsAmount = parseInt(amount);
    if (isNaN(pointsAmount) || pointsAmount === 0) {
      setNotificationMessage({
        message: "Points cannot be zero",
        type: "warning"
      });
      return;
    }
    if (Math.abs(pointsAmount) > 5000) {
      setNotificationMessage({
        message: "Point adjustments are limited to ±5,000 at a time. For larger adjustments, please reach out to our support team:",
        type: "info",
        supportLink: true
      });
      return;
    }

    // Validate child business selection if parent business
    if (isParentBusiness() && !selectedChildBusinessId) {
      setNotificationMessage({
        message: "Please select a child business",
        type: "warning"
      });
      return;
    }

    setIsModalLoading(true);
    try {
      const request: AddPointsRequest = {
        UserId: selectedMember.UserId,
        Points: pointsAmount
      };

      // Use selected child business ID if parent, otherwise use current business ID
      const businessId = isParentBusiness() && selectedChildBusinessId
        ? selectedChildBusinessId
        : state?.currentBusiness?.BusinessId;

      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}${ADD_POINTS_PATH}/${businessId}`,
        request,
        {
          headers: {
            Authorization: `Bearer ${state.token}`,
            "Content-Type": "application/json"
          }
        }
      );

      setNotificationMessage({
        message: `Successfully added ${pointsAmount} points to ${
          selectedMember.NAME || selectedMember.Email || "member"
        }`,
        type: "success"
      });

      // Refresh members data
      refreshMembers();
      handleCloseModal();
    } catch (error: any) {
      console.error("Error adding points:", error);

      // Handle different types of errors
      let errorMessage = "Failed to add points. Please try again.";

      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const data = error.response.data;

        if (status === 400) {
          errorMessage =
            data?.message || "Invalid request. Please check the amount.";
        } else if (status === 401) {
          errorMessage = "Unauthorized. Please check your authentication.";
        } else if (status === 403) {
          errorMessage =
            "Access denied. You don't have permission to perform this action.";
        } else if (status === 404) {
          errorMessage = "Member not found or business not found.";
        } else if (status === 500) {
          errorMessage = "Server error. Please try again later.";
        } else if (data?.message) {
          errorMessage = data.message;
        }
      } else if (error.request) {
        // Network error
        errorMessage =
          "Network error. Please check your connection and try again.";
      }

      setNotificationMessage({
        message: errorMessage,
        type: "error"
      });
    } finally {
      setIsModalLoading(false);
    }
  };

  const handleOpenModal = (member: Member, type: "credit" | "points") => {
    setSelectedMember(member);
    setActionType(type);
    setAmount("");
    setSelectedChildBusinessId(""); // Reset child business selection
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedMember(null);
    setAmount("");
    setSelectedChildBusinessId(""); // Reset child business selection
    setNotificationMessage({ message: "", type: "success" });
  };

  const handleSubmit = () => {
    // Clear any previous messages
    setNotificationMessage({ message: "", type: "success" });

    if (actionType === "credit") {
      handleAddCredit();
    } else {
      handleAddPoints();
    }
  };

  // Helper function to render sortable header
  const renderSortableHeader = (columnId: string, label: string) => {
    const isSorted = sortField === columnId;
    return (
      <MuiBox
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          cursor: "pointer",
          userSelect: "none",
          "&:hover": {
            opacity: 0.7
          }
        }}
        onClick={() => handleSort(columnId)}
      >
        <span>{label}</span>
        {isSorted ? (
          sortDirection === "asc" ? (
            <ArrowUpwardIcon sx={{ fontSize: 16, color: "#192f67" }} />
          ) : (
            <ArrowDownwardIcon sx={{ fontSize: 16, color: "#192f67" }} />
          )
        ) : (
          <UnfoldMoreIcon sx={{ fontSize: 16, color: "rgba(0, 0, 0, 0.26)" }} />
        )}
      </MuiBox>
    );
  };

  // Define table columns for CustomTableWithPagination
  const columns: any = [
    {
      id: "NAME",
      label: "Name",
      minWidth: 200,
      align: "left",
      sortable: true,
      headerFormat: () => renderSortableHeader("NAME", "Name"),
      format: (_value: any, row: any) => (
        <MuiBox sx={{ display: "flex", alignItems: "center" }}>
          {row?.NAME || "-"}
          {row?.NAME && (
            <MuiBox onClick={(e) => e.stopPropagation()}>
              <CopyToClipboardWrapper
                text={row?.UserId?.toString() || ""}
                cell="User ID"
              />
            </MuiBox>
          )}
        </MuiBox>
      )
    },
    {
      id: "Mobile",
      label: "Phone",
      minWidth: 150,
      align: "left",
      sortable: true,
      headerFormat: () => renderSortableHeader("Mobile", "Phone"),
      format: (value: any) => value || "-"
    },
    {
      id: "DateOfBirth",
      label: "Date of Birth",
      minWidth: 200,
      align: "left",
      sortable: true,
      headerFormat: () => renderSortableHeader("DateOfBirth", "Date of Birth"),
      format: (value: any) =>
        value ? getFormattedDateWithoutTime(value) : "-"
    },
    {
      id: "PostCode",
      label: "Post Code",
      minWidth: 100,
      align: "left",
      sortable: false,
      format: (value: any) => value || "-"
    },
    {
      id: "Credits",
      label: "Credits",
      minWidth: 100,
      align: "left",
      sortable: true,
      headerFormat: () => renderSortableHeader("Credits", "Credits"),
      format: (value: any) => `$${value || 0}`
    },
    {
      id: "Points",
      label: "Points",
      minWidth: 100,
      align: "left",
      sortable: true,
      headerFormat: () => renderSortableHeader("Points", "Points"),
      format: (value: any) => value || "0"
    },
    {
      id: "DateSignUp",
      label: "Join Date",
      minWidth: 200,
      align: "left",
      sortable: true,
      headerFormat: () => renderSortableHeader("DateSignUp", "Join Date"),
      format: (value: any) => (value ? getFormattedDate(value) : "-")
    },
    {
      id: "DateLastActivity",
      label: "Last Activity",
      minWidth: 200,
      align: "left",
      sortable: true,
      headerFormat: () => renderSortableHeader("DateLastActivity", "Last Activity"),
      format: (value: any) => (value ? getFormattedDate(value) : "-")
    },
    {
      id: "HasEmail",
      label: "Email Status",
      minWidth: 120,
      align: "left",
      sortable: false,
      format: (value: any) => (value === 1 ? "Yes" : "No")
    },
    {
      id: "actions",
      label: "Actions",
      minWidth: 300,
      align: "left",
      sortable: false,
      format: (_value: any, row: any) => (
        <MuiBox sx={{ display: "flex", gap: "10px", justifyContent: "center" }}>
          <CustomButton
            size="small"
            variant="outlined"
            onClick={(e: any) => {
              e.stopPropagation();
              handleOpenModal(row, "credit");
            }}
            disabled={isNonPaying || isBillingLoading}
          >
            Add Credit
          </CustomButton>
          <CustomButton
            size="small"
            variant="outlined"
            onClick={(e: any) => {
              e.stopPropagation();
              handleOpenModal(row, "points");
            }}
            disabled={isNonPaying || isBillingLoading}
          >
            Add Points
          </CustomButton>
        </MuiBox>
      )
    }
  ];

  return (
    <CommonPageLayout
      heading="Members"
      subHeading="Manage your Members"
      docs={null}
    >
      <MuiBox>
          {isNonPaying && (
            <MuiBox sx={{ mb: 2 }}>
              <Alert severity="error">
                Access limited: Please renew your contract or fix payment issues
                to manage members.
              </Alert>
            </MuiBox>
          )}
          {/* Search Section */}
          <TypographyTitle>
            {"Search Members by name, or email"}
          </TypographyTitle>
          <BoxTitle sx={{ mb: 2 }}>
            <SearchBoxContainer>
              <SearchBox
                label="Search"
                variant="outlined"
                value={searchQuery}
                setSearchTerm={setSearchQuery}
                onKeyPress={handleKeyPress}
              />
              <Button
                variant="contained"
                color="secondary"
                onClick={() => handleSearch(searchQuery)}
                className={
                  !searchQuery.trim()
                    ? "blue_button custom_disabled"
                    : "blue_button"
                }
                disabled={!searchQuery.trim()}
              >
                Search
              </Button>
              {searchQuery && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={clearSearch}
                  className="blue_button"
                >
                  Clear
                </Button>
              )}
            </SearchBoxContainer>
            <RefreshButton onClick={() => {
              if (isSearchMode && searchQuery) {
                searchMembers(searchQuery);
              } else {
                refreshMembers(currentPage + 1, payloadSize);
              }
            }} />
          </BoxTitle>

          {/* Search Error Message */}
          {searchError && (
            <MuiBox sx={{ mb: 2 }}>
              <Alert severity="error" onClose={() => setSearchError("")}>
                {searchError}
              </Alert>
            </MuiBox>
          )}

          <CustomTableWithPagination
            columns={columns}
            data={sortMembers(filteredMembers, sortField, sortDirection)}
            apiStatus={isLoading || isSearching ? "fetching" : "idle"}
            payloadSize={payloadSize}
            currentPage={currentPage}
            totalCount={
              isSearchMode && !serverTotalCount && filteredMembers.length > 0
                ? filteredMembers.length
                : serverTotalCount
            }
            lastEvaluatedKey={null}
            handleChangeRowsPerPage={(event: any) => {
              const nextSize = parseInt(event?.target?.value, 10) || 10;
              setPayloadSize(nextSize);
              setCurrentPage(0);
              // useEffect will handle the API call when payloadSize or currentPage changes
            }}
            handleChangePage={(_e: any, newPage: number) => {
              setCurrentPage(newPage);
              // useEffect will handle the API call when currentPage changes
            }}
            isShowPagination={true}
            noDataFoundMessage={
              "No data available."
            }
            handleRowClick={(row: any) => {
              console.log("🚀 ~ MembersPage ~ row:", row);
              navigate(`/members/${row?.UserId}`);
            }}
            sx={{ cursor: "pointer" }}
          />
      </MuiBox>

      {/* Add Credit/Points Modal */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <MuiBox
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "24px",
                lineHeight: 1.25,
                fontFamily: "Montserrat",
                fontStyle: "normal",
                color: "#192F67"
              }}
            >
              {selectedMember
                ? `Add ${
                    actionType === "credit" ? "Credit" : "Points"
                  } to Member`
                : "Add Credit/Points"}
            </Typography>
            <IconButton
              aria-label="close"
              onClick={handleCloseModal}
              disabled={isModalLoading}
              sx={{
                color: (theme) =>
                  isModalLoading
                    ? theme.palette.grey[400]
                    : theme.palette.grey[500]
              }}
            >
              <CloseIcon />
            </IconButton>
          </MuiBox>
        </DialogTitle>
        <DialogContent dividers>
          {!selectedMember && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Action Type</InputLabel>
              <Select
                value={actionType}
                label="Action Type"
                onChange={(e) =>
                  setActionType(e.target.value as "credit" | "points")
                }
              >
                <MenuItem value="credit">Add Credit</MenuItem>
                <MenuItem value="points">Add Points</MenuItem>
              </Select>
            </FormControl>
          )}

          {/* Child Business Dropdown - Show only if parent business */}
          {isParentBusiness() && (
            <FormControl fullWidth sx={{ mb: 2 }} required>
              <InputLabel>Select Child Business</InputLabel>
              <Select
                value={selectedChildBusinessId}
                label="Select Child Business"
                onChange={(e) => {
                  setSelectedChildBusinessId(e.target.value);
                  // Clear notification when user selects a business
                  if (notificationMessage.message) {
                    setNotificationMessage({ message: "", type: "success" });
                  }
                }}
                disabled={isModalLoading}
              >
                {getChildBusinesses().map((business: any) => (
                  <MenuItem key={business.BusinessId} value={business.BusinessId}>
                    {business.BusinessName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            fullWidth
            label={actionType === "credit" ? "Credit Amount" : "Points"}
            type={actionType === "credit" ? "number" : "number"}
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
              if (actionType === "points") {
                const val = parseInt(e.target.value);
                if (!isNaN(val) && Math.abs(val) > 5000) {
                  setNotificationMessage({
                    message: "Point adjustments are limited to ±5,000 at a time. For larger adjustments, please reach out to our support team:",
                    type: "info",
                    supportLink: true
                  });
                } else {
                  setNotificationMessage({ message: "", type: "success" });
                }
              } else if (notificationMessage.message) {
                setNotificationMessage({ message: "", type: "success" });
              }
            }}
            inputProps={{
              step: actionType === "credit" ? 0.1 : 1,
              min: actionType === "points" ? -5000 : 0,
              max: actionType === "points" ? 5000 : undefined
            }}
            sx={{ mb: 2 }}
            disabled={isModalLoading}
            error={
              notificationMessage.type === "error" ||
              notificationMessage.type === "warning"
            }
            helperText={
              actionType === "credit"
                ? "Enter credit amount (e.g., 10.50)"
                : "Enter points between -5000 and 5000"
            }
          />

          {notificationMessage?.message && (
            <MuiBox sx={{ mt: 2 }}>
              <Alert
                severity={notificationMessage?.type as any}
                onClose={() =>
                  setNotificationMessage({ message: "", type: "success", supportLink: false })
                }
              >
                <MuiBox>
                  {notificationMessage?.message}
                  {notificationMessage?.supportLink && (
                    <MuiBox sx={{ mt: 0.5 }}>
                      <MuiLink
                        href="mailto:support@rewardle.com?subject=Rewardle%20Points%20Adjustment%20Request"
                        sx={{ color: "inherit", fontWeight: 700, textDecoration: "underline", cursor: "pointer" }}
                      >
                        📧 support@rewardle.com
                      </MuiLink>
                    </MuiBox>
                  )}
                </MuiBox>
              </Alert>
            </MuiBox>
          )}
        </DialogContent>
        <DialogActions>
          <CustomButton
            onClick={handleCloseModal}
            disabled={isModalLoading}
            variant="contained"
            className="blue_button"
          >
            Cancel
          </CustomButton>
          <CustomButton
            onClick={handleSubmit}
            disabled={
              isModalLoading ||
              !amount ||
              (actionType === "points"
                ? parseInt(amount) === 0 || isNaN(parseInt(amount)) || Math.abs(parseInt(amount)) > 5000
                : parseFloat(amount) <= 0) ||
              (isParentBusiness() && !selectedChildBusinessId)
            }
            variant="contained"
            className="blue_button"
            sx={{
              minHeight: "36px"
            }}
          >
            {isModalLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              `Add ${actionType === "credit" ? "Credit" : "Points"}`
            )}
          </CustomButton>
        </DialogActions>
      </Dialog>
    </CommonPageLayout>
  );
};

export default MembersPage;
